import React, { useEffect, useState } from 'react';
import {
  AppBar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Container,
  Divider,
  Drawer,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
  Collapse,
  Paper,
  Alert,
  CircularProgress,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import StoreIcon from '@mui/icons-material/Store';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { useAuth } from '../auth/AuthContext';
import { orgApi } from '../api/client';
import OrgAdminPage from './pages/OrgAdminPage';
import EmployeesPage from './pages/EmployeesPage';
import CoursesPage from './pages/CoursesPage';
import AssignmentsPage from './pages/AssignmentsPage';
import LearningPathsPage from './pages/LearningPathsPage';
import AssignLearningPathPage from './pages/AssignLearningPathPage';
import LearningPathProgressPage from './pages/LearningPathProgressPage';
import CertificationCompliancePage from './pages/CertificationCompliancePage';
import WhatsAppPage from './pages/WhatsAppPage';
import SupportPage from './pages/SupportPage';
import InsightsPage from './pages/InsightsPage';
import TrainingReportsPage from './pages/TrainingReportsPage';
import TrainingLeaderboardPage from './pages/TrainingLeaderboardPage';
import QuizAnalyticsPage from './pages/QuizAnalyticsPage';

type CardItem = { key: string; title: string; description: string; icon: string; action: string };

const WelcomeBanner: React.FC<{ title: string; subtitle: string }> = ({ title, subtitle }) => (
  <Card sx={{ mb: 4, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
    <CardContent>
      <Typography variant="h4" gutterBottom>
        {title}
      </Typography>
      <Typography variant="body1">{subtitle}</Typography>
    </CardContent>
  </Card>
);

const ActionGrid: React.FC<{ items: CardItem[]; onOpen: (key: string) => void; md?: number }> = ({
  items,
  onOpen,
  md = 3,
}) => (
  <Grid container spacing={3}>
    {items.map((item) => (
      <Grid item xs={12} sm={6} md={md} key={item.key}>
        <Card
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            transition: 'transform 0.2s',
            '&:hover': { transform: 'translateY(-4px)' },
          }}
        >
          <CardContent sx={{ flexGrow: 1, textAlign: 'center', pt: 3 }}>
            <Box sx={{ color: 'primary.main', mb: 2, fontSize: '3rem' }}>{item.icon}</Box>
            <Typography variant="h6" gutterBottom>
              {item.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {item.description}
            </Typography>
          </CardContent>
          <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
            <Button variant="contained" onClick={() => onOpen(item.key)} fullWidth>
              {item.action}
            </Button>
          </CardActions>
        </Card>
      </Grid>
    ))}
  </Grid>
);

const TopNavPortal: React.FC<{
  title: string;
  menuItems: { key: string; label: string; icon: string }[];
  currentView: string;
  onNavigate: (key: string) => void;
  onSignOut: () => void;
  children: React.ReactNode;
}> = ({ title, menuItems, currentView, onNavigate, onSignOut, children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="static" elevation={2}>
        <Toolbar>
          {isMobile && (
            <IconButton edge="start" color="inherit" aria-label="menu" onClick={() => setMobileMenuOpen(true)} sx={{ mr: 2 }}>
              <MenuIcon />
            </IconButton>
          )}
          <Typography
            variant="h6"
            component="div"
            onClick={() => onNavigate('dashboard')}
            sx={{ flexGrow: 1, cursor: 'pointer', fontWeight: 600 }}
          >
            {title}
          </Typography>
          {!isMobile && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              {menuItems.map((item) => (
                <Button
                  key={item.key}
                  color="inherit"
                  onClick={() => onNavigate(item.key)}
                  variant={currentView === item.key ? 'outlined' : 'text'}
                  sx={{ borderColor: currentView === item.key ? 'inherit' : 'transparent' }}
                >
                  {item.label}
                </Button>
              ))}
            </Box>
          )}
          <Button color="inherit" startIcon={<LogoutIcon />} onClick={onSignOut} sx={{ ml: 2 }}>
            Sign Out
          </Button>
        </Toolbar>
      </AppBar>
      <Drawer anchor="left" open={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)}>
        <Box sx={{ width: 250 }}>
          <Toolbar>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Menu
            </Typography>
          </Toolbar>
          <Divider />
          <List>
            {menuItems.map((item) => (
              <ListItem key={item.key} disablePadding>
                <ListItemButton
                  selected={currentView === item.key}
                  onClick={() => {
                    onNavigate(item.key);
                    setMobileMenuOpen(false);
                  }}
                >
                  <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>{item.icon}</Box>
                  <ListItemText primary={item.label} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>
      <Container maxWidth="lg" sx={{ py: 4, flexGrow: 1 }}>
        {children}
      </Container>
    </Box>
  );
};

const SUPER_MENU = [
  { key: 'dashboard', label: 'Dashboard', icon: '📊' },
  { key: 'business-units', label: 'Business Units', icon: '🏢' },
  { key: 'stores', label: 'Stores', icon: '🏪' },
  { key: 'managers', label: 'Managers', icon: '👥' },
  { key: 'unassigned-employees', label: 'Unassigned Employees', icon: '🚫👥' },
  { key: 'organization', label: 'Organization', icon: '🌳' },
];

const SUPER_CARDS: CardItem[] = [
  { key: 'business-units', title: 'Business Units', description: 'Create and manage business units in your organization.', icon: '🏢', action: 'Manage Business Units' },
  { key: 'stores', title: 'Stores', description: 'Create and manage stores across all business units.', icon: '🏪', action: 'Manage Stores' },
  { key: 'managers', title: 'Managers', description: 'Create and manage managers and assign them to any store.', icon: '👥', action: 'Manage Managers' },
  { key: 'unassigned-employees', title: 'Unassigned Employees', description: 'Review employees who are not yet assigned to a manager.', icon: '🚫👥', action: 'Manage Unassigned Employees' },
  { key: 'organization', title: 'Organization Hierarchy', description: 'View the complete organizational structure: Business Units → Stores → Managers → Employees.', icon: '🌳', action: 'Manage Organization Hierarchy' },
];

const BU_MENU = [
  { key: 'dashboard', label: 'Dashboard', icon: '📊' },
  { key: 'stores', label: 'Stores', icon: '🏪' },
];

const STORE_MENU = [
  { key: 'dashboard', label: 'Dashboard', icon: '📊' },
  { key: 'stores', label: 'Stores', icon: '🏪' },
  { key: 'managers', label: 'Managers', icon: '👥' },
];

const MANAGER_MENU = [
  { key: 'dashboard', label: 'Dashboard', icon: '📊' },
  { key: 'courses', label: 'Courses', icon: '📚' },
  { key: 'employees', label: 'Employees', icon: '👥' },
  { key: 'assignments', label: 'Assignments', icon: '📋' },
  { key: 'analytics', label: 'Analytics', icon: '📈' },
  { key: 'training-status', label: 'Training Status Overview', icon: '📊' },
  { key: 'quiz-analytics', label: 'Quiz Performance Analytics', icon: '📊' },
  { key: 'employees-needing-support', label: 'Employees Needing Support', icon: '🆘' },
  { key: 'whatsapp-messaging', label: 'WhatsApp Messaging', icon: '💬' },
  { key: 'training-reports', label: 'Training Reports', icon: '📄' },
  { key: 'training-leaderboard', label: 'Training Leaderboard', icon: '🏆' },
];

const LP_MENU = [
  { key: 'learning-paths', label: 'Create Learning Path', icon: '🛤️' },
  { key: 'assign-learning-path', label: 'Assign Learning Path', icon: '🎯' },
  { key: 'learning-path-progress', label: 'Learning Path Progress', icon: '📊' },
  { key: 'certification-compliance', label: 'Certification Compliance', icon: '📜' },
];

const MANAGER_CARDS: CardItem[] = [
  { key: 'employees', title: 'Employee Management', description: 'View and manage employee information and assignments.', icon: '👥', action: 'Manage Employees' },
  { key: 'analytics', title: 'Training Analytics', description: 'View training completion rates and progress reports.', icon: '📈', action: 'View Analytics' },
  { key: 'courses', title: 'Course Management', description: 'Create, edit, and manage training courses.', icon: '📚', action: 'Manage Courses' },
  { key: 'learning-paths', title: 'Learning Paths', description: 'Create and manage structured learning paths with multiple courses.', icon: '🛤️', action: 'Manage Learning Paths' },
  { key: 'assign-learning-path', title: 'Assign Learning Path', description: 'Assign learning paths to employees for structured training.', icon: '🎯', action: 'Assign Learning Path' },
  { key: 'learning-path-progress', title: 'Track Learning Path Completion', description: 'Monitor and track employee progress on assigned learning paths.', icon: '📊', action: 'Track Progress' },
  { key: 'assignments', title: 'Course Assignments', description: 'Assign courses to employees for training.', icon: '📋', action: 'Assign Courses' },
  { key: 'training-status', title: 'Training Status Overview', description: 'View overall training status and completion rates.', icon: '📊', action: 'View Status' },
  { key: 'quiz-analytics', title: 'Quiz Performance Analytics', description: 'Analyze quiz performance and question-level statistics.', icon: '📊', action: 'View Analytics' },
  { key: 'employees-needing-support', title: 'Employees Needing Support', description: 'Identify and support employees who need additional training assistance.', icon: '🆘', action: 'View Support' },
  { key: 'whatsapp-messaging', title: 'WhatsApp Messaging', description: 'Send Wati WhatsApp templates and session messages; see employee replies in-app.', icon: '💬', action: 'Open WhatsApp' },
  { key: 'training-reports', title: 'Training Reports', description: 'Generate comprehensive training reports and analytics.', icon: '📄', action: 'View Reports' },
  { key: 'training-leaderboard', title: 'Training Leaderboard', description: 'View employee rankings and achievements in training.', icon: '🏆', action: 'View Leaderboard' },
  { key: 'certification-compliance', title: 'Certification Compliance', description: 'View certifications expiring soon, expired, and valid; reminders and auto re-assignment.', icon: '📜', action: 'View Compliance' },
];

const LP_VIEWS = new Set(['learning-paths', 'assign-learning-path', 'learning-path-progress', 'certification-compliance']);

const ManagerPortal: React.FC = () => {
  const { user, logout } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [currentView, setCurrentView] = useState('store-selection');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [desktopSidebarOpen, setDesktopSidebarOpen] = useState(true);
  const [lpOpen, setLpOpen] = useState(true);
  const [mobileLpOpen, setMobileLpOpen] = useState(false);
  const [stores, setStores] = useState<any[]>([]);
  const [loadingStores, setLoadingStores] = useState(true);
  const [storeError, setStoreError] = useState<string | null>(null);
  const [selectedStoreId, setSelectedStoreId] = useState<string | null>(null);
  const [selectedStoreName, setSelectedStoreName] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadingStores(true);
        const list = await orgApi.myStores();
        if (cancelled) return;
        setStores(list);
        if (!list.length) {
          setStoreError('No stores assigned to this manager. Please contact your administrator.');
        } else if (list.length === 1) {
          setSelectedStoreId(list[0].id);
          setSelectedStoreName(list[0].name);
          setCurrentView('dashboard');
        }
      } catch (e: any) {
        if (!cancelled) setStoreError(e?.response?.data?.error || e.message);
      } finally {
        if (!cancelled) setLoadingStores(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const go = (view: string) => {
    setCurrentView(view);
    setMobileMenuOpen(false);
  };

  if (currentView === 'store-selection') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
        <AppBar position="static" elevation={2}>
          <Toolbar>
            <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600 }}>
              Manager Portal
            </Typography>
            <Button color="inherit" startIcon={<LogoutIcon />} onClick={() => logout()}>
              Sign Out
            </Button>
          </Toolbar>
        </AppBar>
        <Container maxWidth="sm" sx={{ py: 8, flexGrow: 1, display: 'flex', alignItems: 'center' }}>
          <Paper sx={{ p: 4, width: '100%' }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <StoreIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
              <Typography variant="h4" gutterBottom>
                Select Store
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please select which store you want to manage.
              </Typography>
            </Box>
            {loadingStores ? (
              <Box display="flex" justifyContent="center" minHeight={200} alignItems="center">
                <CircularProgress />
              </Box>
            ) : storeError ? (
              <Alert severity="error">{storeError}</Alert>
            ) : (
              <List>
                {stores.map((store, index) => (
                  <React.Fragment key={store.id}>
                    <ListItem disablePadding>
                      <ListItemButton
                        onClick={() => {
                          setSelectedStoreId(store.id);
                          setSelectedStoreName(store.name);
                          setCurrentView('dashboard');
                        }}
                        sx={{ borderRadius: 1, py: 2, '&:hover': { bgcolor: 'primary.light', color: 'white' } }}
                      >
                        <StoreIcon sx={{ mr: 2 }} />
                        <ListItemText primary={store.name} secondary={store.description || null} primaryTypographyProps={{ variant: 'h6' }} />
                      </ListItemButton>
                    </ListItem>
                    {index < stores.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>
        </Container>
      </Box>
    );
  }

  const selectedNav = (key: string) =>
    currentView === key || (key === 'courses' && currentView === 'courses');

  const navButtonSx = {
    '&.Mui-selected': {
      bgcolor: 'primary.main',
      color: 'primary.contrastText',
      '&:hover': { bgcolor: 'primary.dark' },
    },
  };

  const renderContent = () => {
    switch (currentView) {
      case 'courses':
        return (
          <Box>
            <Typography variant="h4" sx={{ mb: 3 }}>
              Course Management
            </Typography>
            <CoursesPage />
          </Box>
        );
      case 'employees':
        return (
          <Box>
            <Typography variant="h4" sx={{ mb: 3 }}>
              Employee Management
            </Typography>
            <EmployeesPage storeId={selectedStoreId || undefined} />
          </Box>
        );
      case 'assignments':
        return (
          <Box>
            <Typography variant="h4" sx={{ mb: 3 }}>
              Course Assignments
            </Typography>
            <AssignmentsPage />
          </Box>
        );
      case 'learning-paths':
        return <LearningPathsPage mode="manage" />;
      case 'assign-learning-path':
        return <AssignLearningPathPage />;
      case 'learning-path-progress':
        return <LearningPathProgressPage />;
      case 'certification-compliance':
        return <CertificationCompliancePage storeId={selectedStoreId} onBack={() => go('dashboard')} />;
      case 'analytics':
        return <InsightsPage variant="analytics" storeId={selectedStoreId} />;
      case 'training-status':
        return <InsightsPage variant="status" storeId={selectedStoreId} />;
      case 'quiz-analytics':
        return <QuizAnalyticsPage storeId={selectedStoreId} />;
      case 'employees-needing-support':
        return (
          <Box>
            <Button onClick={() => go('dashboard')} sx={{ mb: 2 }}>
              Back to Dashboard
            </Button>
            <SupportPage />
          </Box>
        );
      case 'whatsapp-messaging':
        return (
          <Box>
            <Button onClick={() => go('dashboard')} sx={{ mb: 2 }}>
              Back to Dashboard
            </Button>
            <WhatsAppPage />
          </Box>
        );
      case 'training-reports':
        return <TrainingReportsPage storeId={selectedStoreId} onBack={() => go('dashboard')} />;
      case 'training-leaderboard':
        return <TrainingLeaderboardPage storeId={selectedStoreId} onBack={() => go('dashboard')} />;
      default:
        return (
          <Box>
            <WelcomeBanner
              title={`Welcome, ${user?.email || user?.name}!`}
              subtitle="You have successfully logged in to the admin portal with manager privileges."
            />
            <ActionGrid items={MANAGER_CARDS} onOpen={go} />
          </Box>
        );
    }
  };

  const sidebarList = (mobile: boolean) => (
    <List
      sx={
        mobile
          ? undefined
          : {
              flexGrow: 1,
              overflow: 'auto',
              py: 0,
              '& .MuiListItemButton-root': { minHeight: 40, py: 0.25, px: 2 },
            }
      }
    >
      {MANAGER_MENU.map((item) => (
        <ListItem key={item.key} disablePadding>
          <ListItemButton selected={selectedNav(item.key)} onClick={() => go(item.key)} sx={navButtonSx}>
            <Box sx={{ mr: 2, display: 'flex', alignItems: 'center', fontSize: '1.2rem' }}>{item.icon}</Box>
            <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: mobile ? undefined : '0.875rem' }} />
          </ListItemButton>
        </ListItem>
      ))}
      <ListItem disablePadding>
        <ListItemButton
          onClick={() => (mobile ? setMobileLpOpen((v) => !v) : setLpOpen((v) => !v))}
          selected={LP_VIEWS.has(currentView)}
          sx={navButtonSx}
        >
          <Box sx={{ mr: 2, fontSize: '1.2rem' }}>🛤️</Box>
          <ListItemText primary="Learning Paths" primaryTypographyProps={{ fontSize: mobile ? undefined : '0.875rem' }} />
          {(mobile ? mobileLpOpen : lpOpen) ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>
      </ListItem>
      <Collapse in={mobile ? mobileLpOpen : lpOpen} timeout="auto" unmountOnExit>
        <List component="div" disablePadding>
          {LP_MENU.map((item) => (
            <ListItem key={item.key} disablePadding>
              <ListItemButton sx={{ pl: 4, minHeight: 40 }} selected={currentView === item.key} onClick={() => go(item.key)}>
                <Box sx={{ mr: 2 }}>{item.icon}</Box>
                <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: '0.8125rem' }} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Collapse>
    </List>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar
        position="fixed"
        elevation={2}
        sx={{
          zIndex: (t) => t.zIndex.drawer + 1,
          ...(isMobile
            ? {}
            : {
                left: desktopSidebarOpen ? '250px' : 0,
                width: desktopSidebarOpen ? 'calc(100% - 250px)' : '100%',
                transition: 'all 0.3s',
              }),
        }}
      >
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={() => (isMobile ? setMobileMenuOpen(true) : setDesktopSidebarOpen((v) => !v))}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" onClick={() => go('dashboard')} sx={{ flexGrow: 1, cursor: 'pointer', fontWeight: 600 }}>
            Manager Portal
          </Typography>
          <Button color="inherit" startIcon={<LogoutIcon />} onClick={() => logout()} sx={{ ml: 2 }}>
            Sign Out
          </Button>
        </Toolbar>
      </AppBar>

      {!isMobile && (
        <Drawer
          variant="persistent"
          anchor="left"
          open={desktopSidebarOpen}
          sx={{
            width: desktopSidebarOpen ? 250 : 0,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: 250,
              boxSizing: 'border-box',
              height: '100vh',
              top: 0,
              display: 'flex',
              flexDirection: 'column',
            },
          }}
        >
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', borderBottom: '1px solid', borderColor: 'divider', minHeight: 64 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
              MyTraining
            </Typography>
          </Box>
          {sidebarList(false)}
        </Drawer>
      )}

      <Drawer anchor="left" open={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} variant="temporary">
        <Box sx={{ width: 250 }}>
          <Toolbar>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Menu
            </Typography>
          </Toolbar>
          <Divider />
          {sidebarList(true)}
        </Box>
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          mt: '64px',
          ml: !isMobile && desktopSidebarOpen ? '250px' : 0,
          transition: 'margin-left 0.3s',
          width: !isMobile && desktopSidebarOpen ? 'calc(100% - 250px)' : '100%',
        }}
      >
        {selectedStoreName && (
          <Box
            sx={{
              width: '100%',
              px: 3,
              py: 1.5,
              bgcolor: 'primary.light',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              boxSizing: 'border-box',
            }}
          >
            <StoreIcon />
            <Typography variant="h6">Store: {selectedStoreName}</Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setCurrentView('store-selection')}
              sx={{
                ml: 'auto',
                color: 'white',
                borderColor: 'white',
                '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' },
              }}
            >
              Change Store
            </Button>
          </Box>
        )}
        <Container maxWidth="lg" sx={{ py: 4 }}>
          {renderContent()}
        </Container>
      </Box>
    </Box>
  );
};

const SelfHostedApp: React.FC = () => {
  const { user, logout } = useAuth();
  const [view, setView] = useState('dashboard');

  useEffect(() => {
    setView('dashboard');
  }, [user?.id]);

  if (!user) return null;

  if (user.role === 'Managers') return <ManagerPortal />;

  if (user.role === 'BusinessUnit') {
    return (
      <TopNavPortal title="Business Unit Portal" menuItems={BU_MENU} currentView={view} onNavigate={setView} onSignOut={() => logout()}>
        {view === 'stores' ? (
          <Box>
            <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
              Store Management
            </Typography>
            <OrgAdminPage section="stores" />
          </Box>
        ) : (
          <Box>
            <WelcomeBanner
              title="Welcome, Business Unit Administrator!"
              subtitle="You can create and manage stores within your business unit."
            />
            <ActionGrid
              md={4}
              items={[
                { key: 'stores', title: 'Stores', description: 'Create and manage stores in your business unit.', icon: '🏪', action: 'Manage Stores' },
              ]}
              onOpen={setView}
            />
          </Box>
        )}
      </TopNavPortal>
    );
  }

  if (user.role === 'Store') {
    return (
      <TopNavPortal title="Store Portal" menuItems={STORE_MENU} currentView={view} onNavigate={setView} onSignOut={() => logout()}>
        {view === 'stores' && (
          <Box>
            <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
              Store Management
            </Typography>
            <OrgAdminPage section="stores" />
          </Box>
        )}
        {view === 'managers' && (
          <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
            <Typography variant="h4" gutterBottom sx={{ mb: 3, textAlign: 'center' }}>
              Manager Management
            </Typography>
            <OrgAdminPage section="managers" />
          </Box>
        )}
        {view === 'dashboard' && (
          <Box>
            <WelcomeBanner title="Welcome, Store Administrator!" subtitle="You can create and manage stores and managers." />
            <ActionGrid
              md={6}
              items={[
                { key: 'stores', title: 'Stores', description: 'Create and manage stores across business units.', icon: '🏪', action: 'Manage Stores' },
                { key: 'managers', title: 'Managers', description: 'Create and manage managers for your stores.', icon: '👥', action: 'Manage Managers' },
              ]}
              onOpen={setView}
            />
          </Box>
        )}
      </TopNavPortal>
    );
  }

  return (
    <TopNavPortal title="Super Admin Portal" menuItems={SUPER_MENU} currentView={view} onNavigate={setView} onSignOut={() => logout()}>
      {view === 'business-units' && (
        <Box>
          <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
            Business Unit Management
          </Typography>
          <OrgAdminPage section="business-units" />
        </Box>
      )}
      {view === 'stores' && (
        <Box>
          <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
            Store Management
          </Typography>
          <OrgAdminPage section="stores" />
        </Box>
      )}
      {view === 'managers' && (
        <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
          <Typography variant="h4" gutterBottom sx={{ mb: 3, textAlign: 'center' }}>
            Manager Management
          </Typography>
          <OrgAdminPage section="managers" />
        </Box>
      )}
      {view === 'unassigned-employees' && (
        <Box>
          <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
            Unassigned Employees
          </Typography>
          <EmployeesPage unassignedOnly />
        </Box>
      )}
      {view === 'organization' && (
        <Box>
          <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
            Organization Hierarchy
          </Typography>
          <OrgAdminPage section="organization" />
        </Box>
      )}
      {view === 'dashboard' && (
        <Box>
          <WelcomeBanner
            title="Welcome, Super Admin!"
            subtitle="You have full administrative access to manage business units and the entire organization structure."
          />
          <ActionGrid items={SUPER_CARDS} onOpen={setView} />
        </Box>
      )}
    </TopNavPortal>
  );
};

export default SelfHostedApp;
